import { Alert, Spin, Table, Typography } from "antd";

import React, { useEffect, useState } from "react";
import Api from "../api";
import { ReferralCodeDisplay } from "../Components/ReferralCodeDisplay";
import { getUserId, months, showError } from "../utils";
const { Title, Text } = Typography;
type Stats = {
  total_payout: string;
  month: number;
  year: number;
};
type ReferredUser = {
  email: string;
  total_payout: string;
};
export const AffiliateStats = () => {
  const [stats, setStats] = useState<Stats[]>();
  const [referredUsers, setReferredUsers] = useState<ReferredUser[]>();
  const [payoutRate, setPayoutRate] = useState<number>(0);
  const [payoutType, setPayoutType] = useState<"credits" | "cash">("credits");
  const [payoutYears, setPayoutYears] = useState<number>(1);
  const [totalPaidCredits, setTotalPaidCredits] = useState<number>(0);
  const [isDistributor, setIsDistributor] = useState(false);
  const [referredUserCount, setReferredUserCount] = useState(0);
  const userId = getUserId();
  useEffect(() => {
    const fetchStats = async () => {
      if (!userId) return;
      try {
        const res = await Api.get(`/api/users/${userId}/affiliate_stats`);
        setStats(res.stats);
        setReferredUsers(res.referred_users);
        setPayoutRate(res.affiliate_payout_rate);
        setPayoutType(res.affiliate_payout_type);
        setPayoutYears(res.affiliate_payout_years);
        setTotalPaidCredits(res.total_paid_credits);
        setIsDistributor(res.is_distributor);
        setReferredUserCount(res.referred_user_count);
      } catch (error) {
        showError("Failed to get stats", error);
      }
    };
    fetchStats();
  }, [userId]);
  if (!stats || !userId) return <Spin />;
  const formatPayout = (amountCents: number) => {
    const value = Math.round(amountCents / 100);
    if (!value) return "—";
    if (payoutType === "cash") return `€${value}`;
    return `${value} credits`;
  };
  const paidOutColumnTitle =
    payoutType === "cash" ? "Cash paid out (€)" : "Credits paid out";
  const columns = [
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
    },

    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      render: (month: number) => months[month - 1],
    },
    {
      title: paidOutColumnTitle,
      dataIndex: "total_payout",
      key: "total_payout",
      render: (x: string) => formatPayout(+x),
    },
  ];
  return (
    <div style={{ minWidth: 1000 }}>
      <Title>Hero Zone Affiliate Program</Title>
      <Text type="secondary">Your referral code</Text>
      <div style={{ margin: "8px 0 24px" }}>
        <ReferralCodeDisplay userId={userId} />
      </div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="Affiliate payout program"
        description={
          <>
            You earn {payoutRate}% on referred purchases for {payoutYears} year
            {payoutYears > 1 ? "s" : ""} after each referral&apos;s first
            purchase.
            <br />
            {payoutType === "credits" && (
              <>Payouts are added to your account automatically as credits.</>
            )}
            {payoutType === "credits" && (
              <>
                <br />
                Total credits paid out:{" "}
                <Text strong>{formatPayout(totalPaidCredits)}</Text>
              </>
            )}
          </>
        }
      />
      <Title level={4}>
        {payoutType === "cash"
          ? "Monthly cash payouts"
          : "Monthly credits payouts"}
      </Title>
      <Table
        dataSource={stats}
        columns={columns}
        rowKey={(r) => `${r.year}-${r.month}`}
      />
      <ReferredUsersSection
        isDistributor={isDistributor}
        referredUsers={referredUsers}
        referredUserCount={referredUserCount}
        payoutType={payoutType}
        formatPayout={formatPayout}
      />
    </div>
  );
};

const distributorUserColumns = (
  payoutType: "credits" | "cash",
  formatPayout: (amountCents: number) => string
) => [
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
  },
  {
    title: payoutType === "cash" ? "Cash paid (€)" : "Credits paid",
    dataIndex: "total_payout",
    key: "total_payout",
    render: (x: string) => formatPayout(+x),
  },
];

const DistributorReferredUsers = ({
  users,
  payoutType,
  formatPayout,
}: {
  users: ReferredUser[] | undefined;
  payoutType: "credits" | "cash";
  formatPayout: (amountCents: number) => string;
}) => (
  <Table
    dataSource={users}
    columns={distributorUserColumns(payoutType, formatPayout)}
    rowKey="email"
  />
);

const ReferredUsersSection = ({
  isDistributor,
  referredUsers,
  referredUserCount,
  payoutType,
  formatPayout,
}: {
  isDistributor: boolean;
  referredUsers: ReferredUser[] | undefined;
  referredUserCount: number;
  payoutType: "credits" | "cash";
  formatPayout: (amountCents: number) => string;
}) => (
  <>
    <Title level={4}>Referred Users</Title>
    {isDistributor ? (
      <DistributorReferredUsers
        users={referredUsers}
        payoutType={payoutType}
        formatPayout={formatPayout}
      />
    ) : (
      <Text>
        You have referred <Text strong>{referredUserCount}</Text>{" "}
        {referredUserCount === 1 ? "user" : "users"}.
      </Text>
    )}
  </>
);
